import { analytics } from "../../analytics";

/**
 * Identify user in analytics
 * or do nothing if user.id || user.roles are not defined
 *
 * @param {Object} [user] - user object
 * @param {string} [user.id] - user id
 * @param {string[]} [user.roles] - user roles
 *
 * @returns {void}
 */
export function identifyUser(user) {
    if (typeof user?.id !== "string" || !user?.roles) {
        return;
    }

    const roles = Array.isArray(user.roles) ? user.roles.join(", ") : [];

    analytics.identify(user.id, {
        roles,
    });
}

/**
 * Track open report event
 *
 * @param {Object} [payload] - event payload
 * @param {string} [payload.reportId] - report id
 *
 * @returns {void}
 */
export function trackOpenReportEvent(payload) {
    analytics.track("Open Report", payload);
}

/**
 * Track view report event
 *
 * @param {Object} [payload] - event payload
 * @param {string} [payload.reportId] - report id
 * @param {string} [payload.productName] - product name
 * @param {string[]} [payload.sectionNames] - section names
 * @param {string} [payload.viewedSectionName] - section name
 * @param {string} [payload.permission] - report permission
 *
 * @returns {void}
 */
export function trackViewReportEvent(payload) {
    const eventName = payload?.viewedSectionName ? `View Report Section (${payload.viewedSectionName})` : "View Report";

    analytics.track(eventName, payload);
}

/**
 * Click download report PDF event
 *
 * @param {Object} [payload] - event payload
 * @param {string} [payload.reportId] - report id
 * @param {string} [payload.status] - report status (generated, available)
 * @param {string} [payload.url] - report pdf url
 *
 * @returns {void}
 */
export function trackClickDownloadReportPdfEvent(payload) {
    analytics.track("Click Download Report PDF", payload);
}

/**
 * Download report PDF event
 *
 * @param {Object} [payload] - event payload
 * @param {string} [payload.reportId] - report id
 * @param {string} [payload.status] - report status (generated, available)
 * @param {string} [payload.url] - report pdf url
 *
 * @returns {void}
 */
export function trackDownloadReportPdfEvent(payload) {
    analytics.track("Download Report PDF", payload);
}

/**
 * Open report section event
 *
 * @param {Object} [payload] - event payload
 * @param {string} [payload.reportId] - report id
 * @param {string} [payload.productName] - product name
 * @param {string} [payload.sectionName] - section name
 *
 * @returns {void}
 */
export function trackOpenReportSectionEvent(payload) {
    analytics.track("Open Report Section", payload);
}

/**
 * Close report section event
 *
 * @param {Object} [payload] - event payload
 * @param {string} [payload.reportId] - report id
 * @param {string} [payload.productName] - product name
 * @param {string} [payload.sectionName] - section name
 *
 * @returns {void}
 */
export function trackCloseReportSectionEvent(payload) {
    analytics.track("Close Report Section", payload);
}

/**
 * Open report competency score event
 *
 * @param {Object} [payload] - event payload
 * @param {string} [payload.reportId] - report id
 * @param {string} [payload.productName] - product name
 * @param {string} [payload.sectionName] - section name
 * @param {string[]} [payload.competencyNames] - competency name
 * @param {string} [payload.clickedCompetencyHandle] - competency score
 *
 * @returns {void}
 */
export function trackOpenReportCompetencyScoreEvent(payload) {
    const eventName = payload?.clickedCompetencyHandle
        ? `Open Report Competency Score (${payload.clickedCompetencyHandle})`
        : "Open Report Competency Score";

    analytics.track(eventName, payload);
}
